import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/hooks/use-language';
import { SPC_PANELS_PALETTE_LABELS } from '@/lib/products';

interface CatalogFilterProps {
  activeSeries: string;
  activePalette: string;
  activeInstallationType: string;
  activeGroupType: string;
  activeCollection: string;
  activeAccessoryCategory: string;
  onPaletteChange: (value: string) => void;
  onInstallationTypeChange: (value: string) => void;
  onGroupTypeChange: (value: string) => void;
  onCollectionChange: (value: string) => void;
  onAccessoryCategoryChange: (value: string) => void;
  onReset: () => void;
  getCollectionCount: (collection: string) => number;
  getPaletteCount: (palette: string) => number;
  getInstallationTypeCount: (installationType: string) => number;
  getGroupTypeCount: (groupType: string) => number;
  getPanelPaletteCount: (palette: string) => number;
  getAccessoryCategoryCount: (category: string) => number;
}

export const CatalogFilter: React.FC<CatalogFilterProps> = ({
  activeSeries,
  activePalette,
  activeInstallationType,
  activeGroupType,
  activeCollection,
  activeAccessoryCategory,
  onPaletteChange,
  onInstallationTypeChange,
  onGroupTypeChange,
  onCollectionChange,
  onAccessoryCategoryChange,
  onReset,
  getCollectionCount,
  getPaletteCount,
  getInstallationTypeCount,
  getGroupTypeCount,
  getPanelPaletteCount,
  getAccessoryCategoryCount
}) => {
  const { t, language } = useLanguage();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollThumbRef = useRef<HTMLDivElement>(null);
  const filterWrapperRef = useRef<HTMLDivElement>(null);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const palettes = ['ALL', 'WHITE', 'GREY', 'NATURE LIGHT', 'NATURE MEDIUM', 'NATURE DARK', 'STONE'];
  const panelPalettes = ['ALL', 'БЕЛЫЙ', 'СВЕТЛЫЙ', 'СРЕДНИЙ', 'СЕРЫЙ', 'ТЁМНЫЙ', 'КОРИЧНЕВЫЙ', 'СИНЕ-СЕРЫЙ'];

  // Метка палитры SPC PANELS на текущем языке
  const getPanelPaletteLabel = (key: string): string => {
    if (key === 'ALL') return t('catalog.filter.all');
    const labels = SPC_PANELS_PALETTE_LABELS[key];
    if (!labels) return key;
    return labels[(language as 'ru' | 'en' | 'uz')] || labels.ru || key;
  };
  const panelGroups = ['ALL', 'WD', 'STP'];
  // Canonical normalized keys (matching normalizeLayingType in Catalog.tsx)
  const installationTypes = ['ALL', 'layout_tile', 'layout_herringbone', 'layout_plank'];
  // Use uppercase to match DB values (GRAVITY / LINES)
  const carpetCollections = ['ALL', 'GRAVITY', 'LINES'];
  const accessoryCategories = ['ALL', 'SPC LAMINATE', 'SPC PANELS', 'CARPET TILE'];

  // Update scrollbar on scroll, resize, and content changes
  const updateScrollbar = () => {
    const container = scrollContainerRef.current;
    const thumb = scrollThumbRef.current;
    
    if (!container || !thumb) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Check if scrolling is needed
    if (scrollHeight <= clientHeight) {
      setShowScrollbar(false);
      return;
    }
    
    setShowScrollbar(true);

    // Calculate thumb height and position
    const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, 30);
    const trackHeight = clientHeight;
    const thumbPosition = (scrollTop / (scrollHeight - clientHeight)) * (trackHeight - thumbHeight);

    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${thumbPosition}px)`;
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollbar();

    container.addEventListener('scroll', updateScrollbar);
    window.addEventListener('resize', updateScrollbar);

    // MutationObserver to detect content changes (filters expanding/collapsing)
    const observer = new MutationObserver(updateScrollbar);
    observer.observe(container, { childList: true, subtree: true, attributes: true });

    return () => {
      container.removeEventListener('scroll', updateScrollbar);
      window.removeEventListener('resize', updateScrollbar);
      observer.disconnect();
    };
  }, [activeSeries, activePalette, activeInstallationType, activeGroupType, activeCollection, activeAccessoryCategory]);

  // Hover-scroll behavior for small desktop screens
  useEffect(() => {
    const wrapper = filterWrapperRef.current;
    const container = scrollContainerRef.current;
    
    if (!wrapper || !container) return;

    const handleWheel = (e: WheelEvent) => {
      // Only activate on small desktop screens (max-height ~800px or width <= 1440px)
      const isSmallDesktop = window.innerHeight <= 900 || window.innerWidth <= 1440;
      
      if (!isSmallDesktop || !isHovered) {
        return; // Let page scroll normally
      }

      const { scrollTop, scrollHeight, clientHeight } = container;
      const canScrollDown = scrollTop < scrollHeight - clientHeight;
      const canScrollUp = scrollTop > 0;
      const isScrollingDown = e.deltaY > 0;
      const isScrollingUp = e.deltaY < 0;

      // If filter can scroll in the direction user is scrolling, prevent page scroll
      if ((isScrollingDown && canScrollDown) || (isScrollingUp && canScrollUp)) {
        e.preventDefault();
        container.scrollTop += e.deltaY;
      }
      // Otherwise, let the scroll event bubble to page (natural boundary behavior)
    };

    wrapper.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      wrapper.removeEventListener('wheel', handleWheel);
    };
  }, [isHovered]);

  return (
    <aside className="w-full h-full">
      {/* Wrapper with overflow hidden to contain scrollbar */}
      <div 
        ref={filterWrapperRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="relative overflow-hidden mirror-card bg-[#13100B]/80 backdrop-blur-md border-white/5 shadow-2xl h-full"
      >
        {/* Scrollable content container */}
        <div 
          ref={scrollContainerRef}
          className="overflow-y-auto overflow-x-hidden pr-2 catalog-filter-scroll h-full"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            overscrollBehavior: 'contain'
          }}
        >
          <div className="space-y-5 lg:space-y-6 2xl:space-y-5 p-4 lg:p-6 2xl:p-5">
            <div className="flex items-center gap-3 border-b border-white/10 pb-4 2xl:pb-3">
              <div className="w-1 h-1 rounded-full bg-primary shadow-[0_0_10px_rgba(176,138,90,1)]"></div>
              <h3 className="text-[9px] lg:text-[10px] 2xl:text-[9px] font-black uppercase tracking-[0.35em] 2xl:tracking-[0.3em] text-white">
                {activeSeries === 'SPC PANELS' ? t('catalog.filter.design') : 
                 activeSeries === 'CARPET TILE' ? t('catalog.filter.collection') : 
                 activeSeries === 'ACCESSORIES' ? t('catalog.filter.accessory_category') : t('catalog.filter.palette')}
              </h3>
            </div>

            {/* Design Filter for Panels / Palette for Laminate / Collection for Carpet / Accessory Category for Accessories */}
            <div className="space-y-4 2xl:space-y-3">
              <div className="flex flex-col gap-3 2xl:gap-2.5">
                {(activeSeries === 'SPC PANELS' ? panelGroups : 
                  activeSeries === 'CARPET TILE' ? carpetCollections : 
                  activeSeries === 'ACCESSORIES' ? accessoryCategories : palettes).map((p) => (
                  <button 
                    key={p}
                    onClick={() => {
                      if (activeSeries === 'SPC PANELS') onGroupTypeChange(p);
                      else if (activeSeries === 'CARPET TILE') onCollectionChange(p);
                      else if (activeSeries === 'ACCESSORIES') onAccessoryCategoryChange(p);
                      else onPaletteChange(p);
                    }}
                    className={cn(
                      "text-[9px] lg:text-[10px] 2xl:text-[9px] font-black uppercase tracking-[0.18em] 2xl:tracking-[0.15em] text-left transition-all duration-300 flex items-center justify-between group/item",
                      (activeSeries === 'SPC PANELS' ? activeGroupType : 
                       activeSeries === 'CARPET TILE' ? activeCollection : 
                       activeSeries === 'ACCESSORIES' ? activeAccessoryCategory : activePalette) === p ? "text-primary" : "text-white/40 hover:text-white"
                    )}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className={cn(
                        "w-1 h-[1px] transition-all duration-300",
                        (activeSeries === 'SPC PANELS' ? activeGroupType : 
                         activeSeries === 'CARPET TILE' ? activeCollection : 
                         activeSeries === 'ACCESSORIES' ? activeAccessoryCategory : activePalette) === p ? "w-4 bg-primary" : "w-0 bg-white/20 group-hover/item:w-2"
                      )} />
                      {p === 'ALL' ? t('catalog.filter.all') : (
                        activeSeries === 'SPC PANELS' 
                          ? (p === 'WD' ? t('catalog.filter.wood') : t('catalog.filter.stone'))
                          : (activeSeries === 'CARPET TILE' ? p : 
                             activeSeries === 'ACCESSORIES' ? t(`catalog.accessory_category.${p}`) : p)
                      )}
                    </div>
                    <span className="text-[7px] lg:text-[8px] opacity-40">
                      ({activeSeries === 'CARPET TILE' ? getCollectionCount(p) : 
                        activeSeries === 'SPC PANELS' ? getGroupTypeCount(p) : 
                        activeSeries === 'ACCESSORIES' ? getAccessoryCategoryCount(p) : 
                        getPaletteCount(p)})
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Only show second filter for SPC LAMINATE and SPC PANELS */}
            {(activeSeries === 'SPC LAMINATE' || activeSeries === 'SPC PANELS') && (
              <>
                <div className="flex items-center gap-3 border-b border-white/10 pb-4 2xl:pb-3 pt-2">
                  <div className="w-1 h-1 rounded-full bg-primary shadow-[0_0_10px_rgba(176,138,90,1)]"></div>
                  <h3 className="text-[9px] lg:text-[10px] 2xl:text-[9px] font-black uppercase tracking-[0.35em] 2xl:tracking-[0.3em] text-white">
                    {activeSeries === 'SPC PANELS' ? t('catalog.filter.color') : t('catalog.filter.laying_type')}
                  </h3>
                </div>

                <div className="space-y-4 2xl:space-y-3">
                  <div className="flex flex-col gap-3 2xl:gap-2.5">
                    {(activeSeries === 'SPC PANELS' ? panelPalettes : installationTypes).map((i) => (
                      <button 
                        key={i}
                        onClick={() => activeSeries === 'SPC PANELS' ? onPaletteChange(i) : onInstallationTypeChange(i)}
                        className={cn(
                          "text-[9px] lg:text-[10px] 2xl:text-[9px] font-black uppercase tracking-[0.18em] 2xl:tracking-[0.15em] text-left transition-all duration-300 flex items-center justify-between group/item",
                          (activeSeries === 'SPC PANELS' ? activePalette : activeInstallationType) === i ? "text-primary" : "text-white/40 hover:text-white"
                        )}
                      >
                        <div className="flex items-center gap-2.5">
                          <div className={cn(
                            "w-1 h-[1px] transition-all duration-300",
                            (activeSeries === 'SPC PANELS' ? activePalette : activeInstallationType) === i ? "w-4 bg-primary" : "w-0 bg-white/20 group-hover/item:w-2"
                          )} />
                          {i === 'ALL' ? t('catalog.filter.all') : (
                            activeSeries === 'SPC PANELS'
                              ? getPanelPaletteLabel(i)
                              : t(i)
                          )}
                        </div>
                        <span className="text-[7px] lg:text-[8px] opacity-40">
                          ({activeSeries === 'SPC PANELS' ? getPanelPaletteCount(i) : getInstallationTypeCount(i)})
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <button 
              onClick={onReset}
              className="pt-6 2xl:pt-5 w-full text-[8px] lg:text-[9px] 2xl:text-[8px] font-black uppercase tracking-[0.25em] 2xl:tracking-[0.2em] text-white/20 hover:text-primary transition-colors text-left border-t border-white/5 flex items-center gap-2"
            >
              <X size={11} /> {t('catalog.filter.reset')}
            </button>
          </div>
        </div>

        {/* Custom scrollbar track and thumb - positioned inside right edge */}
        {showScrollbar && (
          <div className="absolute top-2 right-2 bottom-2 w-[2px] pointer-events-none z-50">
            {/* Scrollbar thumb */}
            <div 
              ref={scrollThumbRef}
              className="absolute right-0 w-[2px] bg-primary rounded-full transition-all duration-150"
              style={{
                boxShadow: '0 0 8px rgba(176, 138, 90, 0.6)'
              }}
            />
          </div>
        )}
      </div>
    </aside>
  );
};
